import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { receivingApi } from '#services/receiving';
import {
  DEFAULT_TIME_RANGE_PRESET,
  TimeRangeValue,
  buildTimeRange,
} from '#components/time-range.utils';
import { revertAll } from '../actions';

interface ReceivingState {
  filter: string;
  dateRange: TimeRangeValue;
  filterKey: string;
  headerFilters: Record<string, unknown>;
  order: string;
  orderBy: string;
  pageIndex: number;
  rows: unknown[];
  rowCount: number;
  pageSize: number;
  selected: unknown[];
  vendors: Record<string, unknown>;
  skip: boolean;
}

interface GetReceivingsPayload {
  page: number;
  rows: unknown[];
  total: number;
  vendors: Record<string, unknown>;
}

const initialState: ReceivingState = {
  filter: '',
  dateRange: buildTimeRange(DEFAULT_TIME_RANGE_PRESET),
  filterKey: 'id',
  headerFilters: {},
  order: 'desc',
  orderBy: 'id',
  pageIndex: 0,
  rows: [],
  rowCount: 0,
  pageSize: 30,
  selected: [],
  vendors: {},
  skip: false,
};

export const receivingSlice = createSlice({
  name: 'receivingList',
  initialState,
  reducers: {
    setDateRange(state, action: PayloadAction<TimeRangeValue>) {
      state.dateRange = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setFilter(state, action: PayloadAction<string>) {
      state.filter = action.payload;
      state.pageIndex = 0;
      state.skip = true; // Skip query when just typing in the filter box
    },
    setFilterKey(state, action: PayloadAction<string>) {
      state.filterKey = action.payload;
      state.skip = true; // Skip query when only changing filter type
    },
    setHeaderFilters(state, action: PayloadAction<Record<string, unknown>>) {
      state.headerFilters = action.payload;
      state.skip = false;
    },
    setOrder(state, action: PayloadAction<string>) {
      state.order = action.payload;
      state.skip = false;
    },
    setOrderBy(state, action: PayloadAction<string>) {
      state.orderBy = action.payload;
      state.skip = false;
    },
    setPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
      state.skip = false;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      state.pageIndex = 0;
      state.skip = false;
    },
    setRows(state, action: PayloadAction<unknown[]>) {
      state.rows = action.payload;
      state.skip = false;
    },
    setRowCount(state, action: PayloadAction<number>) {
      state.rowCount = action.payload;
      state.skip = false;
    },
    setSelected(state, action: PayloadAction<unknown[]>) {
      const { payload } = action;
      state.selected = payload;
      state.skip = false;
    },
    setSkipFlag(state, action: PayloadAction<boolean>) {
      state.skip = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(revertAll, () => initialState)
      .addMatcher(receivingApi.endpoints.getReceivings.matchFulfilled, (state, action) => {
        const {
          page,
          rows,
          total,
          vendors,
        } = action.payload as GetReceivingsPayload;
        state.pageIndex = page;
        state.rows = rows;
        state.rowCount = total;
        state.vendors = vendors;
        // dont clear the selected data,
        // the selected states should be remembered for the crossing page selection
        // state.selected = [];
      });
  },
});

export const {
  setDateRange,
  setFilter,
  setFilterKey,
  setHeaderFilters,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
  setRows,
  setRowCount,
  setSelected,
  setSkipFlag,
} = receivingSlice.actions;

export default receivingSlice.reducer;
